import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
// REMOVED: import { motion, AnimatePresence } from 'framer-motion'; - for performance
import { WizardStepProps } from '../types';
import { BrokerProvider } from '../../../../data/brokers';
import Trading212CsvUploader from '../../Trading212/Trading212CsvUploader';
import { useUploadTradingCsv } from '../../useUploadTradingCsv';

interface DataUploadStepProps extends WizardStepProps {
  broker: BrokerProvider;
}

const DataUploadStep: React.FC<DataUploadStepProps> = ({
  broker,
  onNext,
  onBack,
  onCancel,
}) => {
  const theme = useTheme();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [uploadResults, setUploadResults] = useState<any[]>([]);

  const uploadMutation = useUploadTradingCsv();

  const handleFilesChange = (files: File[]) => {
    setSelectedFiles(files);
    setError(null);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setError(null);
    setUploadResults([]);
    setUploadProgress({ current: 0, total: selectedFiles.length });

    const results: any[] = [];
    let hasError = false;

    // Upload files sequentially
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setUploadProgress({ current: i + 1, total: selectedFiles.length });

      try {
        const result = await uploadMutation.mutateAsync(file);
        results.push({
          file: file.name,
          success: true,
          data: result,
        });
      } catch (err: any) {
        hasError = true;
        results.push({
          file: file.name,
          success: false,
          error: err.error || 'Upload failed',
        });
        // Continue with remaining files even if one fails
      }
    }

    setUploadResults(results);

    // Check if all uploads succeeded
    const allSucceeded = results.every(r => r.success);

    if (allSucceeded) {
      // Move to next step on success
      setTimeout(() => {
        onNext({ files: selectedFiles, results });
      }, 500);
    } else {
      // Show error summary
      const failedCount = results.filter(r => !r.success).length;
      setError(
        `${failedCount} of ${selectedFiles.length} file(s) failed to upload. Please check the files and try again.`
      );
      setIsUploading(false);
    }
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #fff 0%, #a1a1a6 100%)'
              : 'linear-gradient(135deg, #1d1d1f 0%, #86868b 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Import Your Data
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload your {broker.displayName} transaction history
        </Typography>
      </Box>

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Content area with flex-grow for proper spacing */}
      <Box sx={{ flexGrow: 1, mb: 3 }}>
        <Box sx={{ height: '100%' }}>
          <Box
            sx={{
              p: 4,
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.02)'
                : 'rgba(0, 0, 0, 0.01)',
              borderRadius: 3,
              border: `1px solid ${
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.06)'
              }`,
              minHeight: 300,
            }}
          >
            <Trading212CsvUploader
              broker={broker}
              onFilesChange={handleFilesChange}
              hideNavigation
              embedded
            />
          </Box>
        </Box>
      </Box>

      {/* Upload Progress */}
      {isUploading && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Uploading file {uploadProgress.current} of {uploadProgress.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(uploadProgress.current / uploadProgress.total) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.8) 100%)'
                  : 'linear-gradient(135deg, rgba(22, 163, 74, 1) 0%, rgba(34, 197, 94, 0.9) 100%)',
              },
            }}
          />
        </Box>
      )}

      {/* Footer actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="text"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          disabled={isUploading}
          sx={{ color: 'text.secondary' }}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="text"
            onClick={onCancel}
            disabled={isUploading}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          {selectedFiles.length > 0 && (
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={isUploading}
              startIcon={isUploading ? <CircularProgress size={20} /> : <UploadIcon />}
              sx={{
                minWidth: 160,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.8) 100%)'
                  : 'linear-gradient(135deg, rgba(22, 163, 74, 1) 0%, rgba(34, 197, 94, 0.9) 100%)',
                boxShadow: theme.customShadows.elevated[theme.palette.mode],
                '&:hover': {
                  boxShadow: theme.customShadows.hover[theme.palette.mode],
                },
                '&:disabled': {
                  opacity: 0.7,
                },
              }}
            >
              {isUploading
                ? `Uploading ${uploadProgress.current}/${uploadProgress.total}`
                : `Upload ${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}`}
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default DataUploadStep;